/**
 * Per-campaign copy guidance — the client's copy of the server's rules.
 *
 * `@fias/webapp-client` does not depend on `@fias/db-types`, so the bounds, the
 * platform defaults the wizard pre-fills and the banned-claim matcher (used for
 * live warnings while editing an ad) are mirrored here. The server is the
 * authority — it refuses a banned claim with 422 BANNED_CLAIM on create, edit,
 * approve and deploy — and this copy is pinned to it by
 * `tests/architecture/adopt-copy-guidance-client-parity.test.ts`.
 *
 * Imports nothing, so that test can load it without the client's module graph.
 */

export interface CopyGuidance {
  facts: string[];
  bannedClaims: string[];
}

export const COPY_FACTS_MAX = 20;
export const COPY_FACT_MAX_CHARS = 200;
export const BANNED_CLAIMS_MAX = 50;
export const BANNED_CLAIM_MAX_CHARS = 60;

/** The one-time fee for a first publish, in cents (server: PLUGIN_PUBLISH_FEE_CENTS). */
export const PUBLISH_FEE_CENTS = 5000;

/**
 * The Fias platform defaults — pre-filled for a NEW campaign only, where the
 * person sees and edits them. Never applied to an existing campaign silently.
 */
export const DEFAULT_COPY_GUIDANCE: Readonly<CopyGuidance> = Object.freeze({
  facts: [
    'Visitors from our ads get free trial credits; no credit card needed',
    'You describe your app in plain English and the AI builder builds it in guided steps, usually in minutes',
    'Publishing an app requires a free account',
    `Publishing an app costs a one-time $${PUBLISH_FEE_CENTS / 100} fee the first time it is published; publishing is never free`,
  ],
  bannedClaims: [
    'instantly',
    'instant',
    'in one go',
    'describe once',
    'free to publish',
    'publish for free',
    'publish free',
    'publishing is free',
    'free publishing',
    'unlimited',
    'guaranteed',
  ],
});

/** Lowercase, punctuation → spaces, padded — the space both sides of a match are compared in. */
function claimSpace(text: string): string {
  const words = text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
  return words ? ` ${words} ` : '';
}

/**
 * The first banned claim `text` contains, or null — whole words / phrases,
 * case- and punctuation-insensitive (server: findAdoptBannedClaim).
 */
export function findBannedClaim(text: string, bannedClaims: readonly string[]): string | null {
  const haystack = claimSpace(text);
  if (!haystack) return null;
  for (const claim of bannedClaims) {
    const needle = claimSpace(claim);
    if (needle && haystack.includes(needle)) return claim;
  }
  return null;
}
